/**
 * MeshVoiceManager — Orchestrates mesh-topology voice chat for DrawLab.
 *
 * Each user maintains one WebRTCService per other participant in voice chat.
 * This gives an N×(N-1) mesh where everyone hears everyone, fully peer-to-peer.
 *
 * Glare handling (who initiates the offer):
 *   Deterministic rule: the peer with the LEXICOGRAPHICALLY SMALLER userId
 *   initiates the offer. The other peer waits passively to receive it.
 *   Both peers run the same code; they just take different roles based on
 *   how their IDs compare.
 *
 * Flow when user A (smaller ID) joins voice with user B (larger ID already in voice):
 *   1. A calls joinVoice() → writes to participants/A
 *   2. A's participants listener fires → sees B → creates a WebRTCService for B
 *   3. Since A < B, A is the initiator: A creates offer, writes it to channel A_B
 *   4. B's participants listener fires → sees A → creates a WebRTCService for A
 *   5. Since A < B, B is the receiver: B subscribes to the offer slot on channel A_B
 *   6. B receives A's offer, creates answer, writes to channel A_B
 *   7. A receives B's answer, sets it as remote description
 *   8. Both peers exchange ICE candidates on the same channel
 *   9. Media flows — both can hear each other
 */

import { WebRTCService, AUDIO_CONSTRAINTS, refreshIceServers } from './WebRTCService';
import { SignallingService } from './SignallingService';

const STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
};

export class MeshVoiceManager {
  constructor(roomCode, localUserId, localNickname) {
    this.roomCode = roomCode;
    this.localUserId = localUserId;
    this.localNickname = localNickname;
    this.signalling = new SignallingService(roomCode, localUserId);

    this.state = STATE.IDLE;
    this.localStream = null;
    this.peers = new Map();          // remotePeerId → WebRTCService
    this.remoteStreams = new Map();  // remotePeerId → MediaStream
    this.knownParticipants = new Set(); // track who we've already handled

    // Callbacks set by useVoiceChat hook
    this.onStateChange = null;             // (state)
    this.onPeersChanged = null;             // (peers: Array<{userId, nickname}>)
    this.onRemoteStreamAdded = null;        // (userId, stream)
    this.onRemoteStreamRemoved = null;      // (userId)
    this.onError = null;                    // (error)
  }

  _setState(newState) {
    this.state = newState;
    if (this.onStateChange) this.onStateChange(newState);
  }

  _emitPeersChanged(participants) {
    if (!this.onPeersChanged) return;
    const list = Object.entries(participants || {})
      .filter(([id]) => id !== this.localUserId)
      .map(([userId, data]) => ({ userId, nickname: data.nickname || 'Unknown' }));
    this.onPeersChanged(list);
  }

  /**
   * Determine if the local user should initiate a connection with a given peer.
   * Deterministic rule: smaller userId initiates.
   */
  _shouldInitiate(remotePeerId) {
    return this.localUserId < remotePeerId;
  }

  // ── Entry / Exit ───────────────────────────────────────────────────────────

  /**
   * Join voice chat — acquire mic, mark as participant, subscribe to others.
   */
  async join() {
    if (this.state !== STATE.IDLE) {
      console.warn('[MeshVoice] Already in state:', this.state);
      return;
    }

    this._setState(STATE.CONNECTING);

    try {
      // 1. Acquire local microphone stream
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
        video: false,
      });

      // 1b. Mint TURN credentials before any peer connection is created (the
      // participants listener below drives peer creation). Best-effort — a
      // failure leaves us STUN-only rather than blocking the call.
      await refreshIceServers();

      // 2. Mark self as voice participant
      await this.signalling.joinVoice(this.localNickname);

      // 3. Subscribe to the participants list — this drives peer creation
      this.signalling.subscribeToParticipants((participants) => {
        this._handleParticipantsUpdate(participants);
      });

      this._setState(STATE.CONNECTED);
    } catch (error) {
      console.error('[MeshVoice] Failed to join:', error);
      this._setState(STATE.ERROR);
      if (this.onError) this.onError(error);
      // Clean up partial state
      await this.leave();
    }
  }

  /**
   * Leave voice chat — tear down all peer connections, stop mic, remove from RTDB.
   */
  async leave() {
    // Tear down all peer connections
    for (const [peerId, service] of this.peers.entries()) {
      try {
        service.teardown();
        await this.signalling.clearChannel(peerId);
      } catch { /* non-fatal */ }
    }
    this.peers.clear();
    this.remoteStreams.clear();
    this.knownParticipants.clear();

    // Stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }

    // Remove from participants list and unsubscribe all listeners
    await this.signalling.leaveVoice();
    this.signalling.removeAllListeners();

    this._setState(STATE.IDLE);
  }

  // ── Participants Handling ─────────────────────────────────────────────────

  /**
   * Called when the participants list changes in RTDB.
   * Creates connections for new peers, tears down connections for departed peers.
   */
  async _handleParticipantsUpdate(participants) {
    this._emitPeersChanged(participants);

    const currentIds = new Set(Object.keys(participants || {}));
    currentIds.delete(this.localUserId); // Not a peer of ourselves

    // New peers — create connections
    for (const peerId of currentIds) {
      if (!this.knownParticipants.has(peerId)) {
        this.knownParticipants.add(peerId);
        try {
          await this._createPeerConnection(peerId);
        } catch (err) {
          console.error(`[MeshVoice] Failed to create connection to ${peerId}:`, err);
        }
      }
    }

    // Departed peers — tear down connections
    for (const peerId of this.knownParticipants) {
      if (!currentIds.has(peerId)) {
        this._removePeerConnection(peerId);
      }
    }
  }

  async _createPeerConnection(remotePeerId) {
    const service = new WebRTCService(remotePeerId);
    this.peers.set(remotePeerId, service);

    // Wire up callbacks
    service.onRemoteStream = (peerId, stream) => {
      this.remoteStreams.set(peerId, stream);
      if (this.onRemoteStreamAdded) this.onRemoteStreamAdded(peerId, stream);
    };

    service.onIceCandidate = async (peerId, candidate) => {
      try {
        await this.signalling.writeIceCandidate(peerId, candidate);
      } catch (err) {
        console.warn('[MeshVoice] Failed to write ICE candidate:', err);
      }
    };

    service.onCallEnded = (peerId) => {
      this._removePeerConnection(peerId);
    };

    // Create the peer connection and add our local audio stream
    service.createPeerConnection();
    if (this.localStream) {
      service.addLocalStream(this.localStream);
    }

    // Subscribe to remote ICE candidates for this peer
    this.signalling.subscribeToRemoteCandidates(remotePeerId, async (candidate) => {
      await service.addIceCandidate(candidate);
    });

    if (this._shouldInitiate(remotePeerId)) {
      // We're the initiator: create offer, write it, wait for answer
      const offer = await service.createOffer();
      await this.signalling.writeOffer(remotePeerId, offer);

      // Subscribe to the answer
      this.signalling.subscribeToAnswer(remotePeerId, async (answer) => {
        try {
          await service.setRemoteAnswer(answer);
        } catch (err) {
          console.warn('[MeshVoice] Failed to set remote answer:', err);
        }
      });
    } else {
      // We're the receiver: wait for offer, then answer
      this.signalling.subscribeToOffer(remotePeerId, async (offer) => {
        try {
          const answer = await service.createAnswer(offer);
          await this.signalling.writeAnswer(remotePeerId, answer);
        } catch (err) {
          console.warn('[MeshVoice] Failed to create answer:', err);
        }
      });
    }
  }

  _removePeerConnection(remotePeerId) {
    const service = this.peers.get(remotePeerId);
    if (service) {
      service.teardown();
      this.peers.delete(remotePeerId);
    }
    if (this.remoteStreams.has(remotePeerId)) {
      this.remoteStreams.delete(remotePeerId);
      if (this.onRemoteStreamRemoved) this.onRemoteStreamRemoved(remotePeerId);
    }
    this.knownParticipants.delete(remotePeerId);
    // Clear their signalling channel
    this.signalling.clearChannel(remotePeerId).catch(() => {});
  }

  // ── Controls ───────────────────────────────────────────────────────────────

  /**
   * Toggle local microphone mute.
   * @returns {boolean} new muted state
   */
  toggleMute() {
    if (!this.localStream) return false;
    const track = this.localStream.getAudioTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    return !track.enabled; // muted = true
  }

  get isMuted() {
    const track = this.localStream?.getAudioTracks()[0];
    return track ? !track.enabled : true;
  }
}

export { STATE as MESH_VOICE_STATE };
export default MeshVoiceManager;
